import {
  isArchitect,
  isCsr,
  isEndUser,
  isProductManager,
  isSuperAdmin,
  userHasRole,
  withBaselineUserRole,
  canAccessDevopsWorkspace,
} from "./appRoles";

export const CATALOG_ASSISTANT_ROUTE = "/agent-dashboard/catalog-assistant";

export type AgentAccessRequirement =
  | { kind: "role"; role: string; label: string }
  | { kind: "registered"; label: string }
  | { kind: "devopsWorkspace"; label: string };

export interface AgentCardConfig {
  id: string;
  title: string;
  description: string;
  access: AgentAccessRequirement;
  comingSoon?: boolean;
  route?: string;
}

export const AGENT_DASHBOARD_AGENTS: AgentCardConfig[] = [
  {
    id: "architect",
    title: "Architect Agent",
    description:
      "Design and review API architectures, author platform standards, and publish to the catalog.",
    access: { kind: "role", role: "Architect", label: "Role: Architect" },
    comingSoon: false,
    route: "/agent-dashboard/architect",
  },
  {
    id: "catalog-assistant",
    title: "Catalog Assistant",
    description:
      "Create and edit products, categories, APIs, FAQs, and other catalog entities with AI assistance.",
    access: { kind: "role", role: "product-manager", label: "Role: Product manager" },
    comingSoon: false,
    route: CATALOG_ASSISTANT_ROUTE,
  },
  {
    id: "production-support",
    title: "Production Support Operator Hub",
    description:
      "Look up error codes and messages, ingest SOPs, and get AI-assisted triage for production support.",
    access: { kind: "role", role: "csr", label: "Role: CSR / Prod support" },
    comingSoon: false,
    route: "/agent-dashboard/production-support",
  },
  {
    id: "integration-analyst",
    title: "Integration Support Analyst Agent",
    description:
      "Guide partners through API integration, troubleshooting, and certification.",
    access: { kind: "registered", label: "Registered Users (user / merchant)" },
    comingSoon: false,
    route: "/agent-dashboard/integration-support",
  },
  {
    id: "customer-support",
    title: "Customer Support Agent",
    description:
      "Help end customers with subscriptions, access issues, and product questions.",
    access: { kind: "registered", label: "Registered Users (user / merchant)" },
  },
  {
    id: "devops",
    title: "DevOps Agent",
    description:
      "Query and maintain environments, applications, configurations, and releases.",
    access: {
      kind: "devopsWorkspace",
      label: "Role: Environment manager or DevOps operator",
    },
    comingSoon: false,
    route: "/agent-dashboard/devops",
  },
];

/** Canonical roles assignable in tenant Settings (aliases consolidated). */
export const TENANT_ASSIGNABLE_ROLE_OPTIONS: { id: string; label: string }[] = [
  { id: "Architect", label: "Architect" },
  { id: "ai-product-manager", label: "AI Product Manager" },
  { id: "csr", label: "CSR / Prod support" },
  { id: "prod-support-admin", label: "Prod support admin" },
  { id: "product-manager", label: "Product manager" },
  { id: "gateway-developer", label: "Gateway developer" },
  { id: "environment-manager", label: "Environment manager" },
  { id: "devops-operator", label: "DevOps operator" },
  { id: "tenant-admin", label: "Tenant admin" },
];

/** Dev login options (includes legacy alias names for local testing). */
export const DEV_LOGIN_ROLE_OPTIONS = [
  "Architect",
  "ai-product-manager",
  "csr",
  "prod-support",
  "prod-support-admin",
  "product-manager",
  "gateway-developer",
  "environment-manager",
  "devops-operator",
  "platform-admin",
  "tenant-admin",
  "admin",
  "user",
  "merchant",
  "sami_x_admin",
] as const;

export { userHasRole } from "./appRoles";

export function isRegisteredUser(userRoles: string[]): boolean {
  return isEndUser(withBaselineUserRole(userRoles));
}

export function canAccessAgent(
  userRoles: string[],
  access: AgentAccessRequirement,
): boolean {
  const roles = withBaselineUserRole(userRoles);
  if (isSuperAdmin(roles)) {
    return true;
  }
  if (access.kind === "registered") {
    return isRegisteredUser(roles);
  }
  if (access.kind === "devopsWorkspace") {
    return canAccessDevopsWorkspace(roles);
  }
  const role = access.role;
  if (role.toLowerCase() === "architect") {
    return isArchitect(roles);
  }
  if (role === "csr") {
    return isCsr(roles);
  }
  if (role === "product-manager") {
    return isProductManager(roles);
  }
  return userHasRole(roles, role);
}

export function getAccessibleAgents(userRoles: string[]): AgentCardConfig[] {
  return AGENT_DASHBOARD_AGENTS.filter((agent) =>
    canAccessAgent(userRoles, agent.access),
  );
}
